import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { phoneToTelUri } from '@provider-directory/components/phone-number';
import { LocationIcon } from '../icons/location';
import { PhoneIcon } from '../icons/phone';
import { SocialItem, FooterSocials } from './footer-socials';

export interface ContactsData {
  title: string;
  phone: string;
  location: {
    href: string;
    text: string;
  };
  socials: SocialItem[];
}

interface FooterContactsProps {
  contactsData: ContactsData;
}

export const FooterContacts: React.FC<FooterContactsProps> = ({
  contactsData,
}) => {
  const { key } = useOrgContext();
  const { t } = useTranslation(key);
  const analytics = useAnalytics();
  return (
    <div className={'md:col-span-8 lg:col-auto space-y-32'}>
      <div className={'space-y-16'}>
        <p className={'mf-font-sans mf-text-gray-700 font-bold'}>
          {t(contactsData.title)}
        </p>
        <div className={'space-y-24'}>
          <div
            className={'grid grid-flow-col auto-rows-min justify-start gap-2'}
          >
            <div>
              <span
                className={'mf-icon mf-icon--small mf-icon--gray-dark mt-0.5'}
                data-analytics-click-text={'phone'}
                data-analytics-link-type={'icon'}
              >
                <PhoneIcon
                  className={'mf-icon__svg'}
                  aria-hidden={'true'}
                  focusable={'false'}
                />
              </span>
            </div>

            <div>
              <a
                className={
                  'mf-link mf-link--site-montefiore mf-link--primary mf-link--base mf-link--no-underline mf-text-body-2-sans mf-text-gray-600 font-serif'
                }
                onClick={() => {
                  analytics.navigationClickEvent({
                    navigationType: 'footer',
                    clickText: contactsData.phone,
                    linkType: 'link',
                  });
                  analytics.calledEvent({
                    linkContextPage: 'Home',
                    linkContextComponent: 'FooterContacts',
                    clickText: contactsData.phone,
                    clickURL: phoneToTelUri(contactsData.phone),
                  });
                }}
                href={phoneToTelUri(contactsData.phone)}
                aria-label={t('aria.callLocation', {
                  locationName: t(contactsData.title),
                })}
                data-analytics-click-text={contactsData.phone}
                data-analytics-link-type={'link'}
              >
                <span className={'mf-link__text'}>
                  <span className={'mf-link__text__inner'}>
                    {contactsData.phone}
                  </span>
                </span>
              </a>
            </div>
          </div>
          <div
            className={'grid grid-flow-col auto-rows-min justify-start gap-2'}
          >
            <div>
              <span
                className={'mf-icon mf-icon--small mf-icon--gray-dark mt-0.5'}
                data-analytics-click-text={'location'}
                data-analytics-link-type={'icon'}
              >
                <LocationIcon
                  className={'mf-icon__svg'}
                  aria-hidden={'true'}
                  focusable={'false'}
                />
              </span>
            </div>

            <div>
              <a
                className={
                  'mf-link mf-link--site-montefiore mf-link--primary mf-link--base mf-link--no-underline mf-text-body-2-sans mf-text-gray-600 font-serif'
                }
                onClick={() => {
                  analytics.navigationClickEvent({
                    navigationType: 'footer',
                    clickText: contactsData.location.text,
                    linkType: 'link',
                  });
                  analytics.directionsEvent({
                    linkContextPage: 'Home',
                    linkContextComponent: 'FooterContacts',
                    clickText: contactsData.location.text,
                    clickURL: contactsData.location.href,
                  });
                }}
                href={contactsData.location.href}
                target={'_blank'}
                rel={'noopener noreferrer'}
                aria-label={t('aria.getDirectionsTo', {
                  locationName: t(contactsData.title),
                  address: contactsData.location.text.replace(/\n/g, ', '),
                })}
                data-analytics-click-text={contactsData.location.text}
                data-analytics-link-type={'link'}
              >
                <span className={'mf-link__text'}>
                  {contactsData.location.text.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <FooterSocials socialLinks={contactsData.socials} />
    </div>
  );
};
